// mirrors PUBLIC_API_ROUTES in packages/api-gateway/src/publicApiRoutes.ts, the source of truth

export type ApiRouteGroup =
    | "Authentication"
    | "Workspace Management"
    | "Data Ingest"
    | "Product Retrieval"

export type ApiRouteParam = {
    name: string
    placeholder?: string
    required?: boolean
}

export type ApiRoute = {
    id: string
    group: ApiRouteGroup
    method: "GET" | "POST"
    path: string
    label: string
    summary: string
    query?: ApiRouteParam[]
    body?: ApiRouteParam[]
}

export const API_ROUTE_CATALOG: ApiRoute[] = [
    {
        id: "mint-token",
        group: "Authentication",
        method: "POST",
        path: "/me/api-token",
        label: "Refresh token",
        summary:
            "Generate a new token that expires in 24 hours. This must be called before the current token expires.",
    },
    {
        id: "list-orgs",
        group: "Workspace Management",
        method: "GET",
        path: "/orgs",
        label: "List orgs",
        summary:
            'List every org (workspace) this account belongs to, across all regions. Each result includes its data residency region, so you can pick the right host for that org\'s projects, flights, and epochs. Note that "org" in this API is synonymous with "workspace" in the web app.',
    },
    {
        id: "create-project",
        group: "Workspace Management",
        method: "POST",
        path: "/org/:orgId/projects",
        label: "Create project",
        summary: "Create a project inside an org. Requires editor-level access in the org.",
        body: [{ name: "name", required: true, placeholder: "API Test Project" }],
    },
    {
        id: "list-projects",
        group: "Workspace Management",
        method: "GET",
        path: "/org/:orgId/projects",
        label: "List projects",
        summary: "List every project in an org. Requires viewer-level access.",
    },
    {
        id: "create-flight",
        group: "Workspace Management",
        method: "POST",
        path: "/org/:orgId/project/:projectId/flights",
        label: "Create flight",
        summary:
            "Create a flight and configure how its imagery will be processed. These processing settings cannot be changed later. Requires editor-level access to the project.",
        body: [
            { name: "name", required: true, placeholder: "Canyon Fire Survey" },
            { name: "flightType", required: true, placeholder: "orthomosaic | hotspots" },
            {
                name: "processingMode",
                placeholder: "oneShot (default) | oneShotWithPreview | iterative",
            },
            { name: "imageCount", placeholder: "total images planned, if known" },
        ],
    },
    {
        id: "list-flights",
        group: "Workspace Management",
        method: "GET",
        path: "/org/:orgId/project/:projectId/flights",
        label: "List flights",
        summary: "List every flight in the project. Requires viewer-level access.",
    },
    {
        id: "get-upload-url",
        group: "Data Ingest",
        method: "POST",
        path: "/org/:orgId/project/:projectId/flight/:flightId/images",
        label: "Get upload URL",
        summary:
            "Request a signed URL to upload one image to a flight. Next, PUT the image file's raw bytes to that URL. Successful upload will automatically trigger processing. Requires editor-level access to the project.",
        body: [{ name: "filename", required: true, placeholder: "IMG_0001.JPG" }],
    },
    {
        id: "list-epochs",
        group: "Product Retrieval",
        method: "GET",
        path: "/org/:orgId/project/:projectId/flight/:flightId",
        label: "List epochs",
        summary:
            "Poll a flight's processing epochs. Sorted oldest-first, the last element is the latest processing result. Safe to poll frequently. Alternatively, connect to the websocket described in the docs.",
    },
    {
        id: "ortho-metadata",
        group: "Product Retrieval",
        method: "GET",
        path: "/org/:orgId/project/:projectId/flight/:flightId/epoch/:epochId/ortho/metadata",
        label: "Ortho metadata",
        summary:
            "Query an orthomosaic's geographic bounds and ground sample distance without downloading any imagery.",
    },
    {
        id: "ortho-download",
        group: "Product Retrieval",
        method: "GET",
        path: "/org/:orgId/project/:projectId/flight/:flightId/epoch/:epochId/ortho",
        label: "Ortho (full download)",
        summary:
            "Request a signed URL to a full-resolution orthomosaic. Next, GET that URL to download the Cloud Optimized GeoTIFF file.",
    },
    {
        id: "ortho-preview",
        group: "Product Retrieval",
        method: "GET",
        path: "/org/:orgId/project/:projectId/flight/:flightId/epoch/:epochId/ortho/preview",
        label: "Ortho preview",
        summary:
            "Request a small PNG thumbnail of the orthomosaic, delivered directly in the response body.",
        query: [{ name: "size", placeholder: "512, max 2048" }],
    },
    {
        id: "ortho-export",
        group: "Product Retrieval",
        method: "GET",
        path: "/org/:orgId/project/:projectId/flight/:flightId/epoch/:epochId/ortho/export",
        label: "Ortho export",
        summary:
            "Export an orthomosaic through a signed URL. Next, GET that URL to download the artifact. Choice of format, size, and optional polygon crop.",
        query: [
            { name: "format", placeholder: "jpeg | png | webp | webp-lossless | tiff | kmz" },
            { name: "maxSize", placeholder: "4096, max 8192" },
            { name: "polygon", placeholder: "[[lon,lat], ...]" },
            { name: "filename", placeholder: "export.jpeg" },
        ],
    },
    {
        id: "ortho-tile",
        group: "Product Retrieval",
        method: "GET",
        path: "/org/:orgId/project/:projectId/flight/:flightId/epoch/:epochId/ortho/tile/:z/:x/:y",
        label: "Ortho XYZ tile",
        summary:
            "Request an XYZ tile for an orthomosaic. This route can power a web map's tile-layer (e.g. Deck.gl, MapLibre, Mapbox, QGIS).",
        query: [
            { name: "palette", placeholder: "1-7, optional false-color palette" },
            { name: "opacity", placeholder: "0-100, default 100" },
            { name: "tilesize", placeholder: "256 or 512" },
        ],
    },
    {
        id: "hotspots",
        group: "Product Retrieval",
        method: "GET",
        path: "/org/:orgId/project/:projectId/flight/:flightId/epoch/:epochId/hotspots",
        label: "Hotspots",
        summary:
            "Export hotspot detections through a signed URL. Next, GET that URL to download detections as a GeoJSON file.",
    },
]

export function pathParamNames(path: string): string[] {
    return (path.match(/:[a-zA-Z]+/g) ?? []).map((p) => p.slice(1))
}
